interface DecodedToken {
  exp: number;
  iat: number;
  sub: string;
  aud: string;
  iss: string;
  permissions?: string[];
  scope?: string;
  'https://example.com/roles'?: string[];
  [key: string]: any;
}

/**
 * Decode JWT token without verification (for client-side permission checks only)
 * Note: This is for UI display purposes only. All authorization should still happen on the backend.
 */
export const decodeJWT = (token: string): DecodedToken | null => {
  try {
    console.group('🔍 JWT TOKEN DECODE DEBUG');
    console.log('📝 Token Length:', token.length);
    
    // Split the token and get the payload
    const parts = token.split('.');
    console.log('🧩 Token Parts:', parts.length);
    
    if (parts.length !== 3) {
      console.error('❌ Invalid JWT format - expected 3 parts, got:', parts.length);
      console.groupEnd();
      return null;
    }

    // Decode the base64 payload
    const payload = parts[1];
    console.log('📦 Payload (base64):', payload.substring(0, 50) + '...');
    
    // Add padding if needed
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload);
    console.log('📖 Decoded Payload JSON:', decodedPayload.substring(0, 100) + '...');
    
    const parsed = JSON.parse(decodedPayload) as DecodedToken;
    console.log('✅ Successfully decoded JWT payload');
    console.groupEnd();
    
    return parsed;
  } catch (error) {
    console.error('❌ Error decoding JWT:', error);
    console.groupEnd();
    return null;
  }
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded) return true;
  
  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

/**
 * Extract permissions from token
 * Checks both 'permissions' array and 'scope' string formats
 */
export const getPermissionsFromToken = (token: string): string[] => {
  const decoded = decodeJWT(token);
  if (!decoded) {
    console.log('❌ Could not decode token for permissions');
    return [];
  }

  console.group('🔑 EXTRACTING PERMISSIONS FROM TOKEN');
  let permissions: string[] = [];

  // Check for permissions array (Auth0 custom claims)
  if (decoded.permissions && Array.isArray(decoded.permissions)) {
    console.log('📋 Found permissions array:', decoded.permissions);
    permissions = [...permissions, ...decoded.permissions];
  } else {
    console.log('⚠️ No permissions array found in token');
  }

  // Check for scope string (OAuth2 standard)
  if (decoded.scope && typeof decoded.scope === 'string') {
    const scopes = decoded.scope.split(' ').filter(Boolean);
    console.log('🎯 Found scope string:', decoded.scope, '→ scopes:', scopes);
    permissions = [...permissions, ...scopes];
  } else {
    console.log('⚠️ No scope string found in token');
  }

  // Check for custom role claims  
  if (decoded['https://games-admin-srq.pages.dev/roles'] && Array.isArray(decoded['https://games-admin-srq.pages.dev/roles'])) {
    console.log('👑 Found custom roles:', decoded['https://games-admin-srq.pages.dev/roles']);
    permissions = [...permissions, ...decoded['https://games-admin-srq.pages.dev/roles']];
  } else {
    console.log('⚠️ No custom roles found in token');
  }

  const finalPermissions = Array.from(new Set(permissions)); // Remove duplicates
  console.log('✅ Final permissions list:', finalPermissions);
  console.groupEnd();
  
  return finalPermissions;
};

/**
 * Check if user has specific permission
 */
export const hasPermission = (token: string, permission: string): boolean => {
  const permissions = getPermissionsFromToken(token);
  return permissions.includes(permission);
};

/**
 * Check if user has any of the specified permissions
 */
export const hasAnyPermission = (token: string, permissionList: string[]): boolean => {
  const permissions = getPermissionsFromToken(token);
  return permissionList.some(permission => permissions.includes(permission));
};

/**
 * Get user roles from token
 */
export const getUserRoles = (token: string): string[] => {
  const decoded = decodeJWT(token);
  if (!decoded) {
    console.log('❌ Could not decode token for roles');
    return [];
  }

  console.group('👤 EXTRACTING ROLES FROM TOKEN');
  
  // Check for custom role claims
  if (decoded['https://games-admin-srq.pages.dev/roles'] && Array.isArray(decoded['https://games-admin-srq.pages.dev/roles'])) {
    console.log('👑 Found custom roles:', decoded['https://games-admin-srq.pages.dev/roles']);
    console.groupEnd();
    return decoded['https://games-admin-srq.pages.dev/roles'];
  }

  // Check for other possible role locations
  if (decoded.roles && Array.isArray(decoded.roles)) {
    console.log('👑 Found roles in standard location:', decoded.roles);
    console.groupEnd();
    return decoded.roles;
  }

  console.log('⚠️ No roles found in token');
  console.log('🔍 Available token keys:', Object.keys(decoded));
  console.groupEnd();
  return [];
};

/**
 * Check if user has specific role
 */
export const hasRole = (token: string, role: string): boolean => {
  const roles = getUserRoles(token);
  return roles.includes(role);
};
